import { UiElement } from "../UiElement.js";
import { HTML5Video } from "./HTML5Video.js";
import { TimeConversion } from "./HelperTools.js";
import { Thumbnail } from "./ThumbnailVideoPlayer.js";
import { VectorGraphics } from "./VectorGraphics.js";
import { VideoPlayerUtils } from "./VideoPlayerUtils.js";

export { UiVideoPlayer };

/**
 * Video player class using Shaka Player
 * @extends UiElement
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 */
class UiVideoPlayer extends UiElement {
    /**
     * Build a video player based on Shaka Player and include it to a target element
     * @param {string/HTMLElement} target - HTML target element
     * @param {Object.<string, Array>} options - Dictionary with input configuration for the player. The configuration can be set for the keys media, thumbnail, cover and poster. They contain the respective urls. If media is a list, the player will try to play the next item if the previous ones failed.
     * <pre>
     * Example:
     * const options = {
            'media': ['https://.../stream/master.mpd', 'https://.../stream/master.m3u8'],
            'thumbnail': 'https://.../thumbnail/master.vtt',
            'cover': 'https://.../cover/image.webp,
            'poster': 'https://.../poster/image.jpg',
        }
     * </pre>
     */
    constructor(target, options,) {
        super(target);
        const uiElementContainer = this.getElement();
        uiElementContainer.classList.add("video-player");
        const videoPlayerContainer = document.createElement('div');
        videoPlayerContainer.className = "video-player-container";
        uiElementContainer.appendChild(videoPlayerContainer);

        this.videoPlayer_ = new VideoJSPlayer(videoPlayerContainer, options);
        //this.videoPlayer_ = new ShakaPlayer(videoPlayerContainer, options);
    }

    /**
     * remove the video player completely
     */
    dispose() {
        super.dispose();
        delete this.videoPlayer_;
        return null;
    }

}


class VideoPlayer {
    constructor(target, options) {
        this.videoContainer = target;
        this.mediaUrlList = typeof options['media'] === 'string' ? [options['media']] : options['media']
        this.webvttUrl = options['thumbnail'];
        this.coverUrl = options['cover'];
        this.posterUrl = options['poster'];
        this.initPlayerWithKeyFocus = options['initPlayerWithKeyFocus'];
        this.playbackRatesKeyControl = options['playbackRates']
    }

    /**
     * Apply default video settings provided by the HTML5 video tag
     * @private
     */
    applyHTML5VideoSettings_() {
        HTML5Video.initAudio(this.videoContainer);
        HTML5Video.initKeyControl(this.videoContainer, this.initPlayerWithKeyFocus, this.playbackRatesKeyControl);
        HTML5Video.disablePIP(this.videoContainer);

        let removePosterAfterVideoLoaded;
        let showPosterAfterVideoLoaded;

        if (this.coverUrl == null)
            HTML5Video.poster(this.videoContainer, this.posterUrl, showPosterAfterVideoLoaded = false, removePosterAfterVideoLoaded = true);
        else {
            HTML5Video.poster(this.videoContainer, this.posterUrl, showPosterAfterVideoLoaded = false, removePosterAfterVideoLoaded = false);
            HTML5Video.poster(this.videoContainer, this.coverUrl, showPosterAfterVideoLoaded = true, removePosterAfterVideoLoaded = false);
        }
    }
}

/**
 * Class to build the AWI version of the video.js Player
 * @author ckraemme <ckraemme@awi.de>
 * @memberof vef.ui.media
 * @private
 */
class VideoJSPlayer extends VideoPlayer {
    constructor(target, options) {
        super(target, options);

        this.playerId = "id" + String(Math.floor(Math.random() * 10e6));
        this.playbackRatesKeyControl = [0.1, 0.25, 0.5, 1.0, 1.5, 2.0, 3.0, 5.0, 10.0];

        const dataSetup = {
            controls: true,
            autoplay: false,
            preload: 'auto',
            fluid: true,
            playbackRates: [0.1, 0.5, 1, 2, 10],
            loop: false,
            controlBar: {
                pictureInPictureToggle: false,
                volumePanel: { inline: false },

            }
        }

        // timeTooltip.update:
        // https://github.com/videojs/video.js/issues/7033
        //            data-setup='${JSON.stringify(dataSetup)}'

        this.videoContainer.innerHTML = `
        <div data-vjs-player>
            <video id="${this.playerId}" class="video-js vjs-theme-vef"></video>        
        </div>
        `
        this.videoElement = this.videoContainer.querySelector("video");

        this.mediaUrlList.forEach((mediaSrc) => {
            const mimeTypeFallback = 'video/mp4'; //fallback for 301, 302
            this.videoElement.insertAdjacentHTML('beforeend', `
            <source
            src="${mediaSrc}"
            type="${VideoPlayerUtils.getMimeType(mediaSrc) || mimeTypeFallback}"/>
            `)
        })


        this.videoElement.addEventListener('loadeddata', function () {
            this.videoElement.classList.add("loaded");
            if (this.webvttUrl) {
                const timeElementSelector = `#${this.playerId} .vjs-control .vjs-time-tooltip`;
                new Thumbnail(this.videoContainer, this.webvttUrl, 20, true, '.vjs-progress-control.vjs-control', timeElementSelector);
            }

            if (!HTML5Video.hasAudio(this.videoContainer))
                this.videoJSPlayer.controlBar.removeChild('volumePanel');
        }.bind(this))

        this.applyHTML5VideoSettings_();

        // Note: videojs(this.playerId) is only working when target is already included in the DOM
        this.videoJSPlayer = videojs(target.querySelector('#' + this.playerId), dataSetup)
        window.videoJSPlayer = this.videoJSPlayer;

        const optionsMobileUI = {
            fullscreen: {
                enterOnRotate: true,
                exitOnRotate: true,
                lockOnRotate: true,
                lockToLandscapeOnEnter: false,
                disabled: false
            },
            touchControls: {
                seekSeconds: 10,
                tapTimeout: 300,
                disableOnEnd: false,
                disabled: false,
            }
        };

        this.videoJSPlayer.mobileUi(optionsMobileUI);

        const optionsHttpSourceSelector = {
            default: 'auto'
        };

        //this.videoJSPlayer.ready(function () {
        //}.bind(this))

        this.videoJSPlayer.on('error', () => {
            const backgroundColor = '#F2F2F2';
            this.videoJSPlayer.poster(VectorGraphics.errorImage('Video', backgroundColor));
            this.videoElement.style.backgroundColor = backgroundColor;
            this.videoElement.style.transition = undefined;
            document.getElementById(this.playerId).querySelector('.vjs-error-display.vjs-modal-dialog').style.display = "none";
        })

        //allow dragging of seekbar
        const seekBar = videojs.getComponent('SeekBar');
        seekBar.prototype.handleMouseMove = function handleMouseMove(event) {
            let newTime = this.calculateDistance(event) * this.player_.duration()
            if (newTime === this.player_.duration())
                newTime = newTime - 0.1;
            this.player_.currentTime(newTime);
            this.update();
        }

        // change default videojs time format from hh:mm:ss to hh:mm:ss:f for more precise hover thumbnails 
        const customVideoJSTimeFormat = (currentTime, guideTime) => {
            if (!currentTime)
                return currentTime
            const isoDayTime = TimeConversion.milliseconds2IsoDayTime(currentTime * 1000);
            return isoDayTime

        }
        videojs.time.setFormatTime(customVideoJSTimeFormat);
    }
}


/**
 * Handle shaka player's error messages
 * @author ckraemme <ckraemme@awi.de>
 * @memberof vef.ui.media
 * @private
 */
class ShakaPlayerErrorHandler {
    /**
     * Show the error in detail in the console 
     * @param {Object} error error object
     */
    static onError(error) {
        console.error('Error code', error.code, 'object', error);
    }

    /**
     * Handle event errors
     * @param {Event} event error event
     */
    static onErrorEvent(event) {
        const error = event.detail;
        ShakaPlayerErrorHandler.onError(error);
    }
}


/**
 * Class to build the AWI version of the Shaka Player
 * @author ckraemme <ckraemme@awi.de>
 * @memberof vef.ui.media
 * @private
 */
class ShakaPlayer extends VideoPlayer {
    /**
     * Build the player
     * @param {string/HTMLElement} target - HTML element
     * @param {Object.<string,Array>} options - Dictionary with input configuration for the player. The configuration can be set for the keys media, thumbnail, cover and poster. They contain the respective urls
     */
    constructor(target, options) {
        super(target, options);
        this.manifest;
        this.videoElement = document.createElement('video')
        this.videoElement.className = "video-player-video";
        this.videoContainer.appendChild(this.videoElement);
        this.shakaPlayer = new shaka.Player(this.videoElement);
        this.shakaUI = new shaka.ui.Overlay(this.shakaPlayer, this.videoContainer, this.videoElement);
        this.shakaUIControls = this.shakaUI.getControls();
        this.buildShakaPlayer_()
        window.shakaPlayer = this.shakaPlayer
        window.shakaUI = this.shakaUI;
    }

    /**
     * Configure, initialize and load media into the player. Because the Shaka UI library is used, the player instance is build automatically by the former, that triggers the event 'shaka-ui-loaded' when it is done
     * @private
     */
    buildShakaPlayer_() {
        this.initEventListeners_();
        this.applyHTML5VideoSettings_();
        this.addExperimentalABRRestriction_();
        this.applyShakaSpecificPlayerConfigurations_();
        this.loadVideoManifestAndSelectThumbnailManifest_();
    }

    /**
     * Initialize events to the Shaka Player that extend its functionality, apply configuration and improve error logging.
     * @private
     */
    initEventListeners_() {
        this.shakaPlayer.addEventListener('error', ShakaPlayerErrorHandler.onErrorEvent);
        this.shakaUIControls.addEventListener('error', ShakaPlayerErrorHandler.onErrorEvent);
        this.initEventsAfterVideoHasLoaded_();
    }

    /**
     * Initialize events that should trigger when video has loaded
     * @private
     */
    initEventsAfterVideoHasLoaded_() {
        this.videoContainer.querySelector('video').addEventListener('loadeddata', () => {
            this.videoContainer.querySelector('video').classList.add("loaded");
            this.configureUIShakaPlayer_();
            this.extendFunctionalityForAudio_();
            if (this.webvttUrl)
                new Thumbnail(this.videoContainer, this.webvttUrl, 20, true, '.shaka-seek-bar-container .shaka-seek-bar');
        }, false);
    }

    /**
     * Configure adaptive bitrate restriction in the Shaka Player that are based on the video player size to reduce traffic and loading time. Experimental state.
     * @private
     */
    addExperimentalABRRestriction_() {
        new ResizeObserver(() => {
            if (typeof this.videoContainer !== 'undefined' && this.shakaPlayer.getVariantTracks() !== 'undefined') {
                const realHeight = window.devicePixelRatio * this.videoContainer.clientHeight;
                const manifestHeights = this.shakaPlayer.getVariantTracks().map((x) => {
                    return x.height
                });
                const streamHeight = Math.min.apply(Math, manifestHeights.filter((x) => {
                    return x >= realHeight
                }));
                this.shakaPlayer.configure({
                    'abr': {
                        'restrictions': {
                            'maxHeight': streamHeight
                        }
                    }
                });
            }
        }).observe(this.videoElement);
    }

    /**
     * Method for applying configurations to the Shaka Player provided by its developer
     * <ul>
     * <li> <a href="https://shaka-player-demo.appspot.com/docs/api/shaka.extern.html#.StreamingConfiguration"> Streaming configuration </a> </li>
     * <li> <a href="https://shaka-player-demo.appspot.com/docs/api/shaka.extern.html#.AbrConfiguration"> ABR configuration </a> </li>
     * <li> <a href="https://shaka-player-demo.appspot.com/docs/api/shaka.extern.html#.PlayerConfiguration"> Further player configuration </a> </li>
     * </ul>
     * @private
     */
    applyShakaSpecificPlayerConfigurations_() {

        // Example: this.player.configure("streaming.rebufferingGoal", 4)
        this.shakaPlayer.configure("preferredAudioLanguage", "en");
    }

    /**
     * Selects the webVTT thumbnail manifest based on the video manifest file that can be loaded
     * @private
     */
    loadVideoManifestAndSelectThumbnailManifest_() {
        this.loadMedia_(this.shakaPlayer, this.mediaUrlList).then((manifest) => {
            this.manifest = manifest;
            let index = this.mediaUrlList.indexOf(manifest);
        }).catch(error => {
            console.error("Error", error.message);
        })
    }

    /**
     * Try to load a media file from a list of media paths as long as there are items in the list
     * @param {Object} player - class instance of the player
     * @param {Array} files - files with all media files that the player tries to load in ascending (array position) order
     * @return {string|Promise} When successful, return the path of the media file, else return Promise.reject(reason)
     * @private
     */
    loadMedia_(player, files) {
        function load(path) {
            return player.load(path).then(() => {
                //console.info('Manifest loaded: ' + path);
                return true
            })
                .catch(error => {
                    console.info('Manifest can not be loaded: ' + path);
                    ShakaPlayerErrorHandler.onError(error);
                    return false
                })
        }

        files = files.filter(n => n !== null && n !== undefined);
        return load(files[0]).then(success => {
            if (success) {
                return files[0]
            } else {
                const tmp = files.slice(1, files.length)
                if (tmp == 0) {
                    this.setErrorImage_(player);
                    return Promise.reject(new Error('No supported manifest found! Playback cannot be started!'))
                } else
                    return this.loadMedia_(player, tmp)
            }
        })
    }

    /**
     * Include user notification as poster in case of video error 
     * @param {Object} player - class instance of the player
     * @private
     */
    setErrorImage_(player) {
        const backgroundColor = '#F2F2F2'
        player.getMediaElement().poster = VectorGraphics.errorImage('Video', backgroundColor);
        player.getMediaElement().style.backgroundColor = backgroundColor;
        player.getMediaElement().style.transition = undefined;
    }

    /**
     * Configure the user interface with respect to the media content
     * <ul>
     * <li> <a href="https://shaka-player-demo.appspot.com/docs/api/tutorial-ui-customization.html"> Overflow menu and control panel configuration </a> </li>
     * <li> <a href="https://shaka-player-demo.appspot.com/docs/api/shaka.extern.html#.UIConfiguration"> UI configuration </a> </li>
     * </ul>
     * @private
     */
    configureUIShakaPlayer_() {
        let overflowMenu, controlPanel;

        if (this.shakaPlayer.isAudioOnly()) {
            overflowMenu = ["loop", "playback_rate"];
            controlPanel = ["play_pause", "time_and_duration", "spacer", "mute", "volume", "overflow_menu"]
        }
        else {
            overflowMenu = ["loop", "playback_rate", "language", "captions", "quality"]
            controlPanel = ["play_pause", "time_and_duration", "spacer", "mute", "volume", "fullscreen", "overflow_menu"]
        }

        if (!HTML5Video.hasAudio(this.videoContainer)) {
            overflowMenu = overflowMenu.filter(v => !['language'].includes(v));
            controlPanel = controlPanel.filter(v => !["mute", "volume"].includes(v));
        }

        if (['m3u8', 'mpd'].includes(this.manifest.split('.').pop().toLowerCase())) {
            if (this.shakaPlayer.isLive())
                overflowMenu = overflowMenu.filter(v => !['loop', 'playback_rate'].includes(v));
            if (!this.shakaPlayer.getAudioLanguages() || this.shakaPlayer.getAudioLanguages().length <= 1)
                overflowMenu = overflowMenu.filter(v => !['language'].includes(v));
        } else {
            overflowMenu = overflowMenu.filter(v => !['language', 'quality'].includes(v));
        }

        const keyControlViaHTML5VideoElement = true;
        let uiConfigurationOptions = {
            'controlPanelElements': controlPanel,
            'overflowMenuButtons': overflowMenu,
            'enableKeyboardPlaybackControls': !keyControlViaHTML5VideoElement,
            'playbackRates': [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
            'showUnbufferedStart': true
        };
        this.shakaUI.configure(uiConfigurationOptions);
    }

    /**
     * Extend the functionality of the shaka player by providing a method that prevents the control bar from hiding while playing audio
     * @private
     */
    extendFunctionalityForAudio_() {

        if (this.shakaPlayer.isAudioOnly()) {
            const setUIAlwaysVisible = true;
            const targetNode = this.videoContainer.querySelector('.shaka-controls-container');
            const config = { attributes: true, childList: false, subtree: true };

            const callback = (mutationList, observer) => {
                for (const mutation of mutationList) {
                    if (mutation.type === 'attributes' && mutation.attributeName == 'shown') {
                        if (mutation.target.getAttribute(mutation.attributeName) == undefined)
                            mutation.target.setAttribute("shown", setUIAlwaysVisible);
                    }
                }
            };
            const observer = new MutationObserver(callback);
            observer.observe(targetNode, config);
        }
    }
}
